import { ApiClient } from './client';
import { GymSpaceConfig } from './types';
import { createAgentFetch } from './utils/agent-fetch';
import {
  AuthResource,
  OrganizationsResource,
  GymsResource,
  CollaboratorsResource,
  RolesResource,
  ClientsResource,
  MembershipPlansResource,
  ContractsResource,
  DashboardResource,
  CheckInsResource,
  InvitationsResource,
  AssetsResource,
  FilesResource,
  PublicCatalogResource,
  AdminCatalogResource,
  HealthResource,
  OnboardingResource,
  ProductsResource,
  SalesResource,
  SuppliersResource,
  UsersResource,
  SubscriptionsResource,
  SubscriptionPlansResource,
  AdminSubscriptionManagementResource,
  PaymentMethodsResource,
  WhatsAppResource,
  WhatsAppTemplatesResource,
  BulkMessagingResource,
  ActivitiesResource,
  TagsResource,
  CommissionConfigResource,
  CommissionRulesResource,
  CommissionCalculationsResource,
  CommissionReportsResource,
  CommissionPromotionsResource,
  MessagesResource,
} from './resources';

export class GymSpaceSdk {
  public client: ApiClient;
  private expoFetch?: typeof globalThis.fetch;

  // Resources
  public auth: AuthResource;
  public organizations: OrganizationsResource;
  public gyms: GymsResource;
  public collaborators: CollaboratorsResource;
  public roles: RolesResource;
  public clients: ClientsResource;
  public membershipPlans: MembershipPlansResource;
  public contracts: ContractsResource;
  public dashboard: DashboardResource;
  public checkIns: CheckInsResource;
  public invitations: InvitationsResource;
  public assets: AssetsResource;
  public files: FilesResource;
  public publicCatalog: PublicCatalogResource;
  public adminCatalog: AdminCatalogResource;
  public health: HealthResource;
  public onboarding: OnboardingResource;
  public products: ProductsResource;
  public sales: SalesResource;
  public suppliers: SuppliersResource;
  public users: UsersResource;
  public subscriptions: SubscriptionsResource;
  public subscriptionPlans: SubscriptionPlansResource;
  public adminSubscriptionManagement: AdminSubscriptionManagementResource;
  public paymentMethods: PaymentMethodsResource;
  public whatsapp: WhatsAppResource;
  public whatsappTemplates: WhatsAppTemplatesResource;
  public bulkMessaging: BulkMessagingResource;
  public activities: ActivitiesResource;
  public tags: TagsResource;
  public commissionConfig: CommissionConfigResource;
  public commissionRules: CommissionRulesResource;
  public commissionCalculations: CommissionCalculationsResource;
  public commissionReports: CommissionReportsResource;
  public commissionPromotions: CommissionPromotionsResource;
  public messages: MessagesResource;

  constructor(config: GymSpaceConfig) {
    this.client = new ApiClient(config);

    // Initialize resources
    this.auth = new AuthResource(this.client);
    this.organizations = new OrganizationsResource(this.client);
    this.gyms = new GymsResource(this.client);
    this.collaborators = new CollaboratorsResource(this.client);
    this.roles = new RolesResource(this.client);
    this.clients = new ClientsResource(this.client);
    this.membershipPlans = new MembershipPlansResource(this.client);
    this.contracts = new ContractsResource(this.client);
    this.dashboard = new DashboardResource(this.client);
    this.checkIns = new CheckInsResource(this.client);
    this.invitations = new InvitationsResource(this.client);
    this.assets = new AssetsResource(this.client);
    this.files = new FilesResource(this.client);
    this.publicCatalog = new PublicCatalogResource(this.client);
    this.adminCatalog = new AdminCatalogResource(this.client);
    this.health = new HealthResource(this.client);
    this.onboarding = new OnboardingResource(this.client);
    this.products = new ProductsResource(this.client);
    this.sales = new SalesResource(this.client);
    this.suppliers = new SuppliersResource(this.client);
    this.users = new UsersResource(this.client);
    this.subscriptions = new SubscriptionsResource(this.client);
    this.subscriptionPlans = new SubscriptionPlansResource(this.client);
    this.adminSubscriptionManagement = new AdminSubscriptionManagementResource(this.client);
    this.paymentMethods = new PaymentMethodsResource(this.client);
    this.whatsapp = new WhatsAppResource(this.client);
    this.whatsappTemplates = new WhatsAppTemplatesResource(this.client);
    this.bulkMessaging = new BulkMessagingResource(this.client);
    this.activities = new ActivitiesResource(this.client);
    this.tags = new TagsResource(this.client);
    this.commissionConfig = new CommissionConfigResource(this.client);
    this.commissionRules = new CommissionRulesResource(this.client);
    this.commissionCalculations = new CommissionCalculationsResource(this.client);
    this.commissionReports = new CommissionReportsResource(this.client);
    this.commissionPromotions = new CommissionPromotionsResource(this.client);
    this.messages = new MessagesResource(this.client);
  }

  /**
   * Set the authentication token
   */
  setAuthToken(token: string): void {
    this.client.setAuthToken(token);
  }

  /**
   * Set the refresh token
   */
  setRefreshToken(token: string | null): void {
    this.client.setRefreshToken(token);
  }

  /**
   * Get the current refresh token
   */
  getRefreshToken(): string | null {
    return this.client.getRefreshToken();
  }

  /**
   * Set the current gym context
   */
  setGymId(gymId: string): void {
    this.client.setGymId(gymId);
  }

  /**
   * Clear authentication
   */
  clearAuth(): void {
    this.client.clearAuth();
  }

  /**
   * Set Expo fetch for React Native environments
   * Should be called once during app initialization
   */
  setExpoFetch(fetchFn: typeof globalThis.fetch): void {
    this.expoFetch = fetchFn;
  }

  /**
   * Create a fetch function for agent endpoints
   * Uses the /agent/* proxy that includes request context
   * Automatically uses Expo fetch if set
   */
  createAgentFetch() {
    return createAgentFetch(this.client, this.expoFetch);
  }

  /**
   * Set callback for when tokens are refreshed by backend
   */
  setOnTokenRefreshed(callback: (accessToken: string, refreshToken?: string) => void): void {
    this.client.setOnTokenRefreshed(callback);
  }

  /**
   * Get the underlying API client
   */
  getClient(): ApiClient {
    return this.client;
  }
}
